import React from "react";
import { Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { LeadThread } from "./leadApi";
import { formatEventDate } from "./leadCellUtils";
import { leadConfig, resolveInquirySubject, showsVenueField } from "./leadVertical";

interface Props {
  lead: LeadThread;
}

/**
 * Inquiry-subject column. Historically a venue "Event / Guests / Hall"
 * cell copied from DCC; now vertical-aware.
 *
 * For autism/tutoring verticals: renders the program the parent asked
 * about (from lead_inquiries.custom_fields, resolved through
 * `resolveInquirySubject`), plus optional location and grade metadata.
 * Never renders guest count or a preferred hall.
 *
 * For the venue vertical (DCC): renders the legacy event type + guests +
 * preferred hall as before.
 */
export const EventCell = React.memo(function EventCell({ lead }: Props) {
  const subject = resolveInquirySubject(lead);
  const showGuests = showsVenueField("guest_count");
  const showHall   = showsVenueField("hall");
  const showDate   = showsVenueField("event_date");

  // Primary label — vertical-appropriate.
  const primary = leadConfig.supportsEventFields
    ? [
        lead.event_type
          ? lead.event_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
          : null,
        showGuests && lead.guest_count != null
          ? `${lead.guest_count.toLocaleString()} guests`
          : null,
      ].filter(Boolean).join(" · ")
    : subject.program ?? "";

  // Secondary line — grade / age / location for autism; event date for DCC.
  const secondaryBits = leadConfig.supportsEventFields
    ? [showDate ? formatEventDate(lead.event_date) : null]
    : [subject.location, subject.grade ? `Grade ${subject.grade}` : subject.age ? `Age ${subject.age}` : null];
  const secondary = secondaryBits.filter(Boolean).join(" · ");

  return (
    <Stack spacing={0.5}>
      <Typography sx={{
        fontSize: "0.78rem", fontWeight: 600,
        color: primary ? "#1e293b" : "#94a3b8",
      }}>
        {primary || "—"}
      </Typography>

      {secondary && (
        <Stack direction="row" alignItems="center" gap={0.6}>
          {leadConfig.supportsEventFields
            ? <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
            : <SchoolOutlinedIcon        sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />}
          <Typography sx={{ fontSize: "0.71rem", color: "#64748b" }}>
            {secondary}
          </Typography>
        </Stack>
      )}

      {showHall && lead.preferred_hall && (
        <Stack direction="row" alignItems="center" gap={0.6}>
          <RoomOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.67rem", color: "#64748b" }}>
            {lead.preferred_hall}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
});
